import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import styles from './DealOverviewChart.module.scss';
import classNames from 'classnames/bind';
import { Box } from '@mui/material';
import "react-datepicker/dist/react-datepicker.css";
import BaseSelectCustom from '~/components/common/base-select-custom/BaseSelectCustom';
import { useAppDispatch } from '~/redux/hooks';
import { handleDashboardJobMothly } from '~/thunks/dashboard/dashboard';

interface DealOverviewProps {
  className?: string;
  classTitle?: string;
}

const DealOverview = ({ className, classTitle }: DealOverviewProps) => {
  const cx = classNames.bind(styles);
  const [timeRange, setTimeRange] = useState<string>('Yearly');
  const dispatch = useAppDispatch();
  const [chartData, setChartData] = useState<any[]>([])
  const [timeTarget, setTimeTarget] = React.useState(new Date());

  const GradientBar = (props: any) => {
    const { x, y, height, width, value, label, month } = props;
    const [hovered, setHovered] = React.useState(false);

    return (
      <g
        className="bar-group"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <defs>
          <linearGradient id="barBorderGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2D80FC" />
            <stop offset="100%" stopColor="#111E33" />
          </linearGradient>

          <linearGradient id="hoverAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(45, 128, 252, 0.05)" />
            <stop offset="100%" stopColor="rgba(45, 128, 252, 0.2)" />
          </linearGradient>
        </defs>

        {/* Hover area */}
        <rect
          x={x + width / 2 - 28}
          y={y - 24}
          width={56}
          height={height + 84}
          fill={hovered ? "url(#hoverAreaGradient)" : "transparent"}
          rx={27}
        />

        {/* Bar */}
        <rect
          x={x + width / 2 - 0.5}
          y={y}
          width={1}
          height={height}
          stroke="url(#barBorderGradient)"
          strokeWidth={1}
          fill="none"
        />

        {/* Bar Point */}
        {!hovered && (
          <circle cx={x + width / 2} cy={y - 10} r={5} fill="#2D80FC" />
        )}

        {/* Value of Bar */}
        {hovered && (
          <>
            <rect
              x={x + width / 2 - 28}
              y={y - 30}
              width={56}
              height={28}
              rx={12}
              fill="#2D80FC"
            />
            <text
              x={x + width / 2}
              y={y - 14}
              textAnchor="middle"
              fontSize={12}
              fill="#fff"
              dominantBaseline="middle"
            >
              {value}
            </text>
          </>
        )}

        {/* Bar Label */}
        <circle
          cx={x + width / 2}
          cy={y + height + 30}
          r={18}
          fill={hovered ? "#2D80FC" : "#111E33"}
        />
        <text
          x={x + width / 2}
          y={y + height + 32}
          textAnchor="middle"
          fontSize={12}
          fill="#fff"
          dominantBaseline="middle"
        >
          {month}
        </text>
      </g>
    );
  };

  const getTextDateTarget = (date: Date) => {
    let month = date.getMonth();
    if (timeRange == "Quarterly") {
      let index = Math.round(month / 3) + 1;
      return "Quater " + index;
    }
    else if (timeRange == "Monthly") {
      return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(date));
    }
    else if (timeRange == "Weekly") {

      const year = date.getFullYear();
      const month = date.getMonth();

      const dayOfMonth = date.getDate();

      const weekIndex = Math.floor((dayOfMonth - 1) / 7);

      const startDay = weekIndex * 7 + 1;

      let endDay = startDay + 6;

      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      if (endDay > lastDayOfMonth) endDay = lastDayOfMonth;

      // Giới hạn trong tháng
      const startDate = new Date(year, month, startDay);
      const endDate = new Date(year, month, endDay);

      return `${startDate.getDate()} - ${endDate.getDate()}, ${
        new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(date))
      }`;
    }
    return "";
  }

  const fetchData = () => {
    dispatch(handleDashboardJobMothly({
      filter: {
        filter: timeRange,
        takeDate: timeTarget,
      }
    })).unwrap()
      .then((reponse: any) => {
        let data = reponse.data;
        let charts: any[] = [];
        data.map((item: any, index: number) => {
          charts.push({
            month: timeRange == "Yearly" ? item.time.slice(0, 3) :
            timeRange == "Quarterly" ? item.time.slice(0, 3) :
             item.time.split("/")[0],
            value: item.totalJobs,
          })
        })

        setChartData(charts);
      })
      .catch((error: any) => {

      })
      .finally(() => {

      });
  }

  const onClickPre = () => {
    const now = new Date();
    let preDate = new Date(timeTarget)

    if (timeRange == "Quarterly")
      preDate.setMonth(preDate.getMonth() - 3);
    else if (timeRange == "Monthly") {
      preDate.setMonth(preDate.getMonth() - 1);
    }
    else if (timeRange == "Weekly") {
      const year = preDate.getFullYear();
      const month = preDate.getMonth();
      let dayOfMonth = preDate.getDate();

      if(dayOfMonth > 1 && dayOfMonth - 7 < 1)
        preDate.setDate(-1);
      else
        preDate.setDate(dayOfMonth - 7);
    }

    const oneYearLater = new Date(preDate);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
    if (now <= oneYearLater) {
      setTimeTarget(preDate);
    }
  }

  const onClickNext = () => {
    const now = new Date();
    let nextDate = new Date(timeTarget)

    if (timeRange == "Quarterly")
      nextDate.setMonth(nextDate.getMonth() + 3);
    else if (timeRange == "Monthly") {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
    else if (timeRange == "Weekly") {
      const year = nextDate.getFullYear();
      const month = nextDate.getMonth();
      const dayOfMonth = nextDate.getDate();

      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      if(dayOfMonth + 7 > lastDayOfMonth && dayOfMonth < lastDayOfMonth)
        nextDate.setDate(lastDayOfMonth);
      else
        nextDate.setDate(nextDate.getDate() + 7);
    }
    const oneYearLater = new Date(nextDate);
    // oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
    if (now >= nextDate) {
      setTimeTarget(nextDate);
    }
  }

  useEffect(() => {
    fetchData();
  }, [timeRange, timeTarget])


  return (
    <Box className={cx('DealOverview', className)}>
      <Box className={cx('header')}>
        <h2 className={cx('title', classTitle)}>Deal overview</h2>
        <div className='flex items-center gap-[24px]'>
          {
            timeRange != "Yearly" && <div className={cx('control-time')}>
              <div className={cx('button-action')} onClick={onClickPre}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="#949699" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>

              <h4 className={cx('text-time')} style={{
                width: timeRange == "Weekly" ? "140px" : "80px"
              }}>{getTextDateTarget(timeTarget)}</h4>

              <div className={cx('button-action')} onClick={onClickNext}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="#949699" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
            </div>
          }

          <Box className={cx('controls')}>
            <BaseSelectCustom
              selectedView={timeRange}
              onViewChange={(viewId: any) => {
                setTimeRange(viewId);
              }}
              showCheckmark={false}
              viewOptions={[
                { id: "Weekly", label: "Weekly" },
                { id: "Monthly", label: "Monthly" },
                { id: "Quarterly", label: "Quarterly" },
                { id: "Yearly", label: "Yearly" },
              ]}
              bgDropdown="#0C192D"
              // placeholder="---"
              className={cx('select-filter')}
              minWidth={'100px'}
              IconDrop={
                <div className={cx('drop')}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.95906 4.47461L6.69906 7.73461C6.31406 8.11961 5.68406 8.11961 5.29906 7.73461L2.03906 4.47461" stroke="#F9FBFE" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              }
            />
          </Box>
        </div>

      </Box>

    <Box className="w-full h-[280px] outline-none" style={{ overflowX: 'auto' }}>
      <div style={{minWidth: chartData.length > 12 ? (chartData.length * 92 - 56) : "100%", height: 270 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 40, right: 20, left: 20, bottom: 50 }}>
            <Bar dataKey="value" fill="#8884d8" shape={<GradientBar />} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        </div>
      </Box>

    </Box>
  );
};


export default DealOverview;